import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import DuoModal from '../../components/ui/duo-modal';
import { APP_API_URL } from '../../utils';
import formatNumber, { defaultFilterDates, formatDate } from '../../utils/functions';
import Update from './update';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

export default function Customers() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCustomerForBalance, setSelectedCustomerForBalance] = useState(null);
  const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedHistoryCustomer, setSelectedHistoryCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [transactionType, setTransactionType] = useState("credit");
  const [addBalanceLoading, setAddBalanceLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [totalBalance, setTotalBalance] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [responseMeta, setResponseMeta] = useState(null);
  const [metaExtra, setMetaExtra] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const [filters, setFilters] = useState(() => ({
    search: '',
    period: '',
    ...defaultFilterDates(),
  }));
  const [appliedSearch, setAppliedSearch] = useState('');

  useEffect(() => {
    if (!invoiceData) return;

    const timer = setTimeout(() => {
      const el = document.getElementById("printable-invoice");
      if (!el) return;

      window.print();
      setInvoiceData(null);
    }, 700);

    return () => clearTimeout(timer);
  }, [invoiceData]);

  const handlePrint = (transactionData) => {
    setInvoiceData(transactionData);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      const searching = appliedSearch.trim() !== '';
      const response = await axios.get(`${APP_API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          search: searching ? appliedSearch.trim() : undefined,
          period: searching ? undefined : filters.period || undefined,
          date_from: searching ? undefined : filters.dateFrom || undefined,
          date_to: searching ? undefined : filters.dateTo || undefined,
        }
      });

      const responseData = response.data.data;
      setData(responseData || []);
      setLastPage(response.data.meta?.last_page || 1);
      setResponseMeta(response.data.meta);
      setMetaExtra(response.data.meta_extra);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortConfig, filters.period, filters.dateFrom, filters.dateTo, appliedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Actions de Pagination
  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) {
      setCurrentPage(prev => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  // Déclencheur de Tri
  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({ search: '', period: '', ...defaultFilterDates() });
    setAppliedSearch('');
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleOpenAddBalance = (customer) => {
    setSelectedCustomerForBalance(customer);
    setAmount('');
    setDescription('');
    setShowAddBalanceModal(true);
  };

  const handleAddBalance = async (e) => {
    setAddBalanceLoading(true);
    e.preventDefault();

    if (!selectedCustomerForBalance || !amount || parseFloat(amount) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Montant invalide',
        text: 'Veuillez entrer un montant valide supérieur à 0.',
        confirmButtonText: 'OK',
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/customer-transactions`,
        {
          customer_id: selectedCustomerForBalance.id,
          type: transactionType,
          amount: parseFloat(amount),
          description: description.trim() || `Ajout au solde - ${new Date().toLocaleDateString('fr-FR')}`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: response.data.message || 'Solde mis à jour avec succès ! Facture générée.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745',
        }).then(() => {

          window.location.href = "/balance/invoice/" + response.data.data.id

        });
        fetchData();
        setShowAddBalanceModal(false);
        setAmount('');
        setDescription('');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: response.data.message || 'Erreur lors de l\'ajout',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545',
        });
      }
    } catch (error) {
      console.error('Erreur ajout solde:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Une erreur est survenue lors de l\'ajout au solde',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setAddBalanceLoading(false);
    }
  };

  const handleDeleteTransaction = async (id) => {
    setIsLoading(true);
    const result = await Swal.fire({
      title: 'Supprimer cette transaction ?',
      text: "Cette action va modifier le solde du client",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#d33',
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');

      await axios.delete(`${APP_API_URL}/customer-transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire('Supprimé', 'Transaction supprimée avec succès', 'success');

      fetchCustomerHistory(selectedHistoryCustomer.id);
      fetchData();

    } catch (error) {
      Swal.fire('Erreur', 'Impossible de supprimer la transaction', 'error');
    } finally {
      setIsLoading(false)
    }
  };

  const handleOpenHistory = (customer) => {
    // console.log(customer)
    setSelectedHistoryCustomer(customer);
    setCustomerHistory([]);           // reset
    fetchCustomerHistory(customer.id);
    setShowHistoryModal(true);
  };

  const fetchCustomerHistory = useCallback(async (customerId) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${APP_API_URL}/customer-transactions/customer/${customerId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCustomerHistory(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      Swal.fire({ // Rafraîchit la liste des clients avec le nouveau solde
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de charger l\'historique des transactions',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Gestion des ventes</p>
          <h1 className="duo-hero-title">Clients</h1>
          <p className="duo-hero-sub mt-2 mb-0">Gérez vos clients, soldes et historiques.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total soldes clients</p>
            <p className="duo-kpi-chip__value">
              {formatNumber(metaExtra?.total_balance ?? 0)} FCFA
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Clients affichés</p>
            <p className="duo-kpi-chip__value">{responseMeta?.total ?? data.length}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des clients
            </span>
            <div className="duo-toolbar__actions">
              <button
                type="button"
                title="Rafraîchir"
                onClick={fetchData}
                className="duo-btn duo-btn--ghost duo-btn--icon"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={toggleAccordion}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setCurrentPage(1);
                  setAppliedSearch(filters.search.trim());
                }}
              >
                <div className="duo-filters__grid">
                  <input
                    type="search"
                    className="duo-input"
                    placeholder="Rechercher..."
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    value={filters.dateTo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={filters.period}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        period: e.target.value,
                        dateFrom: '',
                        dateTo: '',
                      }))
                    }
                  >
                    <option value="">Période</option>
                    <option value="today">Aujourd&apos;hui</option>
                    <option value="yesterday">Hier</option>
                    <option value="this_week">Cette semaine</option>
                    <option value="this_month">Ce mois-ci</option>
                    <option value="this_year">Cette année</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2 flex-wrap">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Rechercher
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>
                    Nom <SortIcon active={sortConfig.key === 'name'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('client_code')}>
                    Code <SortIcon active={sortConfig.key === 'client_code'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('email')}>
                    Email <SortIcon active={sortConfig.key === 'email'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('numero')}>
                    Tél. <SortIcon active={sortConfig.key === 'numero'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Créé <SortIcon active={sortConfig.key === 'created_at'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('fidelite')}>
                    Fidélité <SortIcon active={sortConfig.key === 'fidelite'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('balance')}>
                    Solde <SortIcon active={sortConfig.key === 'balance'} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="duo-empty-row">
                      Chargement des clients…
                    </td>
                  </tr>
                ) : data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={`Customer-${item.id}-${index}`}>
                      <td>{item.name ?? 'N/A'}</td>
                      <td>{item.client_code ?? 'N/A'}</td>
                      <td>{item.email ?? 'N/A'}</td>
                      <td>{item.numero ?? 'N/A'}</td>
                      <td>{item.date_creation ? formatDate(item.date_creation) : 'N/A'}</td>
                      <td>
                        <span
                          className={`duo-badge-pill ${
                            item.fidelite === 'Actif' ? 'duo-badge-pill--ok' : 'duo-badge-pill--off'
                          }`}
                        >
                          {item.fidelite === 'Actif' ? 'Active' : item.fidelite ? 'Inactive' : 'N/A'}
                        </span>
                      </td>
                      <td className="text-nowrap">{formatNumber(item.balance)} FCFA</td>
                      <td>
                        <div className="duo-table__actions">
                          {!isRole13 && (
                            <>
                              <button
                                type="button"
                                title="Modifier"
                                className="duo-btn duo-btn--ghost duo-btn--sm"
                                data-bs-toggle="modal"
                                data-bs-target={`#updateModal${item.id}`}
                              >
                                <i className="bi bi-pencil-square" />
                              </button>
                              <button
                                type="button"
                                title="Ajouter au solde"
                                className="duo-btn duo-btn--primary duo-btn--sm"
                                onClick={() => handleOpenAddBalance(item)}
                              >
                                <i className="bi bi-plus-circle" />
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            title="Historique"
                            className="duo-btn duo-btn--blue duo-btn--sm"
                            onClick={() => handleOpenHistory(item)}
                          >
                            <i className="bi bi-clock-history" />
                          </button>
                          <Link
                            to={`/clients/${item.id}/factures`}
                            title="Voir toutes les factures"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                          >
                            <i className="bi bi-receipt" />
                          </Link>
                          <Update item={item} refreshData={fetchData} />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="duo-empty-row">
                      Aucun client correspondant aux critères de recherche.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {responseMeta?.from || 0} – {responseMeta?.to || 0} sur {responseMeta?.total || 0} entrée
                {(responseMeta?.total || 0) > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage === lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {showAddBalanceModal && selectedCustomerForBalance && (
        <DuoModal
          title={`Solde — ${selectedCustomerForBalance.name}`}
          onClose={() => setShowAddBalanceModal(false)}
        >
          <div className="duo-balance-box">
            Solde actuel : <strong>{formatNumber(selectedCustomerForBalance.balance)} FCFA</strong>
          </div>
          <form onSubmit={handleAddBalance}>
            <div className="duo-form-group">
              <label htmlFor="type" className="duo-form-label">
                Type d&apos;opération *
              </label>
              <select
                id="type"
                className="duo-select"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value)}
                required
              >
                <option value="credit">Crédit</option>
                <option value="debit">Débit</option>
              </select>
            </div>
            <div className="duo-form-group">
              <label htmlFor="amount" className="duo-form-label">
                Montant *
              </label>
              <input
                type="number"
                id="amount"
                className="duo-input"
                placeholder="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="100"
                step="100"
                required
              />
            </div>
            <div className="duo-form-group">
              <label htmlFor="description" className="duo-form-label">
                Description (optionnel)
              </label>
              <textarea
                id="description"
                className="duo-input"
                rows="3"
                placeholder="Recharge, paiement mobile..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <button type="submit" className="duo-btn duo-btn--primary w-100" disabled={addBalanceLoading}>
              {addBalanceLoading ? 'Traitement...' : "Confirmer l'opération"}
            </button>
          </form>
        </DuoModal>
      )}

      {showHistoryModal && selectedHistoryCustomer && (
        <DuoModal
          size="xl"
          title={`Historique — ${selectedHistoryCustomer.name}`}
          onClose={() => {
            setShowHistoryModal(false);
            setCustomerHistory([]);
          }}
          footer={
            <button
              type="button"
              className="duo-btn duo-btn--ghost"
              onClick={() => {
                setShowHistoryModal(false);
                setCustomerHistory([]);
              }}
            >
              Fermer
            </button>
          }
        >
          <div className="duo-balance-box">
            Solde actuel : <strong>{formatNumber(selectedHistoryCustomer.balance)} FCFA</strong>
          </div>
          {historyLoading ? (
            <p className="duo-empty-row">Chargement...</p>
          ) : (
            <div className="duo-table-wrap p-0">
              <table className="duo-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Montant</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customerHistory.length > 0 ? (
                    customerHistory.map((trans) => (
                      <tr key={trans.id}>
                        <td>{formatDate(trans.created_at)}</td>
                        <td>
                          <span
                            className={`duo-badge-pill ${
                              trans.type === 'credit' ? 'duo-badge-pill--ok' : 'duo-badge-pill--off'
                            }`}
                          >
                            {trans.type === 'credit' ? 'Ajout' : 'Retrait'}
                          </span>
                        </td>
                        <td className="text-nowrap">{formatNumber(trans.amount)} FCFA</td>
                        <td>{trans.description || '—'}</td>
                        <td>
                          <div className="duo-table__actions">
                            <Link
                              to={`/balance/invoice/${trans.id}`}
                              className="duo-btn duo-btn--primary duo-btn--sm"
                            >
                              <i className="bi bi-file-earmark-pdf" />
                            </Link>
                            <button
                              type="button"
                              className="duo-btn duo-btn--danger duo-btn--sm"
                              onClick={() => handleDeleteTransaction(trans.id)}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="duo-empty-row">
                        Aucune transaction pour ce client.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DuoModal>
      )}
    </MainLayout>
  );
}